/**
 * Rotas por shell - funcao PURA (sem vue-router, sem env), portanto testavel
 * OP-20260810-004 (B2): dois shells por Host
 *
 * shell 'admin' -> rotas de gestao SaaS (Dashboard, Usuarios, Perfis, Planos,
 *                  Features). SEM cadastros do ERP.
 * shell 'app'   -> rotas do App ERP (Cadastros / Categorias). SEM gestao.
 *
 * Os NOMES das rotas sao os mesmos de antes da separacao - views, stores e
 * testes existentes dependem deles (ERRO-3e356048).
 */

import { SHELLS } from '@/shell'

// Lazy loading de views
const LoginView = () => import('@/views/LoginView.vue')
const AppLayout = () => import('@/components/layout/AppLayout.vue')
const DashboardView = () => import('@/views/DashboardView.vue')

// Gestao SaaS: Users
const UsersListView = () => import('@/views/UsersListView.vue')
const UserFormView = () => import('@/views/UserFormView.vue')

// Gestao SaaS: Roles
const RolesListView = () => import('@/views/RolesListView.vue')
const RoleFormView = () => import('@/views/RoleFormView.vue')

// Gestao SaaS: Planos
const PlanosListView = () => import('@/views/PlanosListView.vue')
const PlanoFormView = () => import('@/views/PlanoFormView.vue')

// Gestao SaaS: Features
const FeaturesListView = () => import('@/views/FeaturesListView.vue')
const FeatureFormView = () => import('@/views/FeatureFormView.vue')

// App ERP: Cadastros - Categorias (OP-20260810-002)
const CategoriasListView = () => import('@/views/cadastros/CategoriasListView.vue')
const CategoriaFormView = () => import('@/views/cadastros/CategoriaFormView.vue')

// App ERP: Cadastros - Marcas (OP-20260813-001)
const MarcasListView = () => import('@/views/cadastros/MarcasListView.vue')
const MarcaFormView = () => import('@/views/cadastros/MarcaFormView.vue')

// App ERP: Cadastros - Redes (OP-20260819-001 F2)
// Entra ANTES de Bandeiras: a rede e a dona da bandeira (FK rede_id).
const RedesListView = () => import('@/views/cadastros/RedesListView.vue')
const RedeFormView = () => import('@/views/cadastros/RedeFormView.vue')

// App ERP: Cadastros - Bandeiras (OP-20260819-001)
const BandeirasListView = () => import('@/views/cadastros/BandeirasListView.vue')
const BandeiraFormView = () => import('@/views/cadastros/BandeiraFormView.vue')

// App ERP: Cadastros - Tipos de Embalagem (OP-20260819-001 F2)
// Entra ANTES de Embalagens: o tipo e a FK da embalagem (tipo_embalagem_id).
const TiposEmbalagemListView = () => import('@/views/cadastros/TiposEmbalagemListView.vue')
const TipoEmbalagemFormView = () => import('@/views/cadastros/TipoEmbalagemFormView.vue')

// App ERP: Cadastros - Embalagens (OP-20260819-001 F2)
const EmbalagensListView = () => import('@/views/cadastros/EmbalagensListView.vue')
const EmbalagemFormView = () => import('@/views/cadastros/EmbalagemFormView.vue')

// App ERP: Cadastros - Produtos Base (OP-20260819-001 F2)
const ProdutosBaseListView = () => import('@/views/cadastros/ProdutosBaseListView.vue')
const ProdutoBaseFormView = () => import('@/views/cadastros/ProdutoBaseFormView.vue')

// App ERP: Cadastros - Produtos (OP-20260921-002 A.4). Entra DEPOIS de Produtos
// Base (o produto aponta para o produto base) e ANTES de Formas de Venda (a
// forma aponta para o produto).
const ProdutosListView = () => import('@/views/cadastros/ProdutosListView.vue')
const ProdutoFormView = () => import('@/views/cadastros/ProdutoFormView.vue')

// App ERP: Cadastros - Formas de Venda (OP-20260901-005 M3b)
// Entra DEPOIS de Produtos e Embalagens: a forma de venda e o vinculo entre as
// duas (produto_id + embalagem_id), e nao existe sem as duas pontas cadastradas.
const FormasVendaListView = () => import('@/views/cadastros/FormasVendaListView.vue')
const FormaVendaFormView = () => import('@/views/cadastros/FormaVendaFormView.vue')

/** Rotas filhas exclusivas do shell 'admin' (gestao SaaS). */
const ROTAS_GESTAO = [
  {
    path: '',
    name: 'Dashboard',
    component: DashboardView,
    meta: {
      title: 'Dashboard',
      breadcrumb: 'Início'
    }
  },
  {
    path: 'users',
    name: 'UsersList',
    component: UsersListView,
    meta: {
      title: 'Usuarios',
      breadcrumb: 'Sistema / Usuarios'
    }
  },
  {
    path: 'users/new',
    name: 'UserCreate',
    component: UserFormView,
    meta: {
      title: 'Novo Usuario',
      breadcrumb: 'Sistema / Usuarios / Novo'
    }
  },
  {
    path: 'users/:id/edit',
    name: 'UserEdit',
    component: UserFormView,
    meta: {
      title: 'Editar Usuario',
      breadcrumb: 'Sistema / Usuarios / Editar'
    }
  },
  {
    path: 'roles',
    name: 'RolesList',
    component: RolesListView,
    meta: {
      title: 'Perfis',
      breadcrumb: 'Sistema / Perfis'
    }
  },
  {
    path: 'roles/new',
    name: 'RoleCreate',
    component: RoleFormView,
    meta: {
      title: 'Novo Perfil',
      breadcrumb: 'Sistema / Perfis / Novo'
    }
  },
  {
    path: 'roles/:id/edit',
    name: 'RoleEdit',
    component: RoleFormView,
    meta: {
      title: 'Editar Perfil',
      breadcrumb: 'Sistema / Perfis / Editar'
    }
  },
  {
    path: 'roles/:id/users',
    name: 'RoleUsers',
    component: RolesListView,
    meta: {
      title: 'Usuarios do Perfil',
      breadcrumb: 'Sistema / Perfis / Usuarios'
    }
  },
  {
    path: 'planos',
    name: 'PlanosList',
    component: PlanosListView,
    meta: {
      title: 'Planos',
      breadcrumb: 'Sistema / Planos'
    }
  },
  {
    path: 'planos/new',
    name: 'PlanoCreate',
    component: PlanoFormView,
    meta: {
      title: 'Novo Plano',
      breadcrumb: 'Sistema / Planos / Novo'
    }
  },
  {
    path: 'planos/:id/edit',
    name: 'PlanoEdit',
    component: PlanoFormView,
    meta: {
      title: 'Editar Plano',
      breadcrumb: 'Sistema / Planos / Editar'
    }
  },
  {
    path: 'features',
    name: 'FeaturesList',
    component: FeaturesListView,
    meta: {
      title: 'Features',
      breadcrumb: 'Sistema / Features'
    }
  },
  {
    path: 'features/new',
    name: 'FeatureCreate',
    component: FeatureFormView,
    meta: {
      title: 'Nova Feature',
      breadcrumb: 'Sistema / Features / Nova'
    }
  },
  {
    path: 'features/:id/edit',
    name: 'FeatureEdit',
    component: FeatureFormView,
    meta: {
      title: 'Editar Feature',
      breadcrumb: 'Sistema / Features / Editar'
    }
  }
]

/** Rotas filhas exclusivas do shell 'app' (App ERP). */
const ROTAS_APP = [
  {
    // Home do App ERP: hoje a unica tela e Categorias
    path: '',
    redirect: '/cadastros/categorias'
  },
  {
    path: 'cadastros/categorias',
    name: 'CategoriasList',
    component: CategoriasListView,
    meta: {
      title: 'Categorias',
      breadcrumb: 'Cadastros / Categorias'
    }
  },
  {
    path: 'cadastros/categorias/new',
    name: 'CategoriaCreate',
    component: CategoriaFormView,
    meta: {
      title: 'Nova Categoria',
      breadcrumb: 'Cadastros / Categorias / Nova'
    }
  },
  {
    path: 'cadastros/categorias/:id/edit',
    name: 'CategoriaEdit',
    component: CategoriaFormView,
    meta: {
      title: 'Editar Categoria',
      breadcrumb: 'Cadastros / Categorias / Editar'
    }
  },
  {
    path: 'cadastros/marcas',
    name: 'MarcasList',
    component: MarcasListView,
    meta: {
      title: 'Marcas',
      breadcrumb: 'Cadastros / Marcas'
    }
  },
  {
    path: 'cadastros/marcas/new',
    name: 'MarcaCreate',
    component: MarcaFormView,
    meta: {
      title: 'Nova Marca',
      breadcrumb: 'Cadastros / Marcas / Nova'
    }
  },
  {
    path: 'cadastros/marcas/:id/edit',
    name: 'MarcaEdit',
    component: MarcaFormView,
    meta: {
      title: 'Editar Marca',
      breadcrumb: 'Cadastros / Marcas / Editar'
    }
  },
  {
    path: 'cadastros/redes',
    name: 'RedesList',
    component: RedesListView,
    meta: {
      title: 'Redes',
      breadcrumb: 'Cadastros / Redes'
    }
  },
  {
    path: 'cadastros/redes/new',
    name: 'RedeCreate',
    component: RedeFormView,
    meta: {
      title: 'Nova Rede',
      breadcrumb: 'Cadastros / Redes / Nova'
    }
  },
  {
    path: 'cadastros/redes/:id/edit',
    name: 'RedeEdit',
    component: RedeFormView,
    meta: {
      title: 'Editar Rede',
      breadcrumb: 'Cadastros / Redes / Editar'
    }
  },
  {
    path: 'cadastros/bandeiras',
    name: 'BandeirasList',
    component: BandeirasListView,
    meta: {
      title: 'Bandeiras',
      breadcrumb: 'Cadastros / Bandeiras'
    }
  },
  {
    path: 'cadastros/bandeiras/new',
    name: 'BandeiraCreate',
    component: BandeiraFormView,
    meta: {
      title: 'Nova Bandeira',
      breadcrumb: 'Cadastros / Bandeiras / Nova'
    }
  },
  {
    path: 'cadastros/bandeiras/:id/edit',
    name: 'BandeiraEdit',
    component: BandeiraFormView,
    meta: {
      title: 'Editar Bandeira',
      breadcrumb: 'Cadastros / Bandeiras / Editar'
    }
  },
  {
    path: 'cadastros/tipos-embalagem',
    name: 'TiposEmbalagemList',
    component: TiposEmbalagemListView,
    meta: {
      title: 'Tipos de Embalagem',
      breadcrumb: 'Cadastros / Tipos de Embalagem'
    }
  },
  {
    path: 'cadastros/tipos-embalagem/new',
    name: 'TipoEmbalagemCreate',
    component: TipoEmbalagemFormView,
    meta: {
      title: 'Novo Tipo de Embalagem',
      breadcrumb: 'Cadastros / Tipos de Embalagem / Novo'
    }
  },
  {
    path: 'cadastros/tipos-embalagem/:id/edit',
    name: 'TipoEmbalagemEdit',
    component: TipoEmbalagemFormView,
    meta: {
      title: 'Editar Tipo de Embalagem',
      breadcrumb: 'Cadastros / Tipos de Embalagem / Editar'
    }
  },
  {
    path: 'cadastros/embalagens',
    name: 'EmbalagensList',
    component: EmbalagensListView,
    meta: {
      title: 'Embalagens',
      breadcrumb: 'Cadastros / Embalagens'
    }
  },
  {
    path: 'cadastros/embalagens/new',
    name: 'EmbalagemCreate',
    component: EmbalagemFormView,
    meta: {
      title: 'Nova Embalagem',
      breadcrumb: 'Cadastros / Embalagens / Nova'
    }
  },
  {
    path: 'cadastros/embalagens/:id/edit',
    name: 'EmbalagemEdit',
    component: EmbalagemFormView,
    meta: {
      title: 'Editar Embalagem',
      breadcrumb: 'Cadastros / Embalagens / Editar'
    }
  },
  {
    path: 'cadastros/produtos-base',
    name: 'ProdutosBaseList',
    component: ProdutosBaseListView,
    meta: {
      title: 'Produtos Base',
      breadcrumb: 'Cadastros / Produtos Base'
    }
  },
  {
    path: 'cadastros/produtos-base/new',
    name: 'ProdutoBaseCreate',
    component: ProdutoBaseFormView,
    meta: {
      title: 'Novo Produto Base',
      breadcrumb: 'Cadastros / Produtos Base / Novo'
    }
  },
  {
    path: 'cadastros/produtos-base/:id/edit',
    name: 'ProdutoBaseEdit',
    component: ProdutoBaseFormView,
    meta: {
      title: 'Editar Produto Base',
      breadcrumb: 'Cadastros / Produtos Base / Editar'
    }
  },
  {
    // 'cadastros/produtos' e 'cadastros/produtos-base' sao segmentos LITERAIS
    // distintos: nao ha conflito de rota entre as duas telas.
    path: 'cadastros/produtos',
    name: 'ProdutosList',
    component: ProdutosListView,
    meta: {
      title: 'Produtos',
      breadcrumb: 'Cadastros / Produtos'
    }
  },
  {
    path: 'cadastros/produtos/new',
    name: 'ProdutoCreate',
    component: ProdutoFormView,
    meta: {
      title: 'Novo Produto',
      breadcrumb: 'Cadastros / Produtos / Novo'
    }
  },
  {
    path: 'cadastros/produtos/:id/edit',
    name: 'ProdutoEdit',
    component: ProdutoFormView,
    meta: {
      title: 'Editar Produto',
      breadcrumb: 'Cadastros / Produtos / Editar'
    }
  },
  {
    path: 'cadastros/formas-venda',
    name: 'FormasVendaList',
    component: FormasVendaListView,
    meta: {
      title: 'Formas de Venda',
      breadcrumb: 'Cadastros / Formas de Venda'
    }
  },
  {
    path: 'cadastros/formas-venda/new',
    name: 'FormaVendaCreate',
    component: FormaVendaFormView,
    meta: {
      title: 'Nova Forma de Venda',
      breadcrumb: 'Cadastros / Formas de Venda / Nova'
    }
  },
  {
    path: 'cadastros/formas-venda/:id/edit',
    name: 'FormaVendaEdit',
    component: FormaVendaFormView,
    meta: {
      title: 'Editar Forma de Venda',
      breadcrumb: 'Cadastros / Formas de Venda / Editar'
    }
  }
]

/**
 * Monta a tabela de rotas do shell informado.
 * Login e catch-all existem nos dois shells.
 *
 * @param {string} shell 'app' | 'admin'
 * @returns {Array} definicao de rotas do vue-router
 */
export function buildRoutes(shell) {
  const filhas = shell === SHELLS.APP ? ROTAS_APP : ROTAS_GESTAO

  return [
    {
      path: '/login',
      name: 'Login',
      component: LoginView,
      meta: {
        requiresAuth: false,
        title: 'Login'
      }
    },
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [...filhas]
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ]
}
